import objectToQueryString from 'shared/helpers/objectToQueryString'

/**
 * Build a set of possible values based on the values assigned to each key in the routes enum
 * */
interface Route {
  id: string
  pathname?: string
  url: (keys?: object) => string
  authRequired?: boolean
}

interface OutletSlug {
  outletSlug: string
}

/**
 * This is to hold all of the links that point to the existing app for when we need it.
 *
 * Remember to add the next/link to the sections using these path as <a> if you are replacing
 * the legacyAppRoot with just '/'.
 */
const legacyAppRoot = process.env.NEXT_PUBLIC_EOLA_BUSINESS_ROOT
export const LEGACY_ROUTES = {
  OUTLETS: {
    id: 'OUTLETS',
    url: () => legacyAppRoot + 'outlets'
  },
  OUTLET: {
    id: 'OUTLET',
    url: ({ outletSlug }: OutletSlug) => legacyAppRoot + `outlets/${outletSlug}`
  },
  SIGN_IN: {
    id: 'SIGN_IN',
    url: () => legacyAppRoot + 'instructors/sign_in'
  },
  CREDIT_VOUCHER: {
    id: 'CREDIT_VOUCHER',
    url: ({
      outletSlug,
      creditVoucherId
    }: OutletSlug & {
      creditVoucherId: string
    }) =>
      legacyAppRoot +
      `outlets/${outletSlug}/vouchers/credit_vouchers/${creditVoucherId}`
  },
  BOOKING: {
    id: 'BOOKING',
    url: ({
      outletSlug,
      schedulableSlug,
      timeSlotId
    }: OutletSlug & {
      schedulableSlug: string
      timeSlotId: string
    }) =>
      legacyAppRoot +
      `outlets/${outletSlug}/schedulables/${schedulableSlug}/time_slots/${timeSlotId}`
  },
  CALENDAR: {
    id: 'CALENDAR',
    url: ({ outletSlug }: OutletSlug) =>
      legacyAppRoot + `outlets/${outletSlug}/time_slots`
  },
  ACTIVITY_VOUCHER: {
    id: 'ACTIVITY_VOUCHER',
    url: ({
      outletSlug,
      activityVoucherId
    }: OutletSlug & {
      activityVoucherId: string
    }) =>
      legacyAppRoot +
      `outlets/${outletSlug}/vouchers/activity_vouchers/${activityVoucherId}`
  },
  CUSTOMER: {
    id: 'CUSTOMER',
    url: ({
      outletSlug,
      enthusiastId
    }: OutletSlug & {
      enthusiastId: string
    }) => legacyAppRoot + `outlets/${outletSlug}/customers/${enthusiastId}`
  },
  MEMBERSHIP_SUBSCRIPTION: {
    id: 'MEMBERSHIP_SUBSCRIPTION',
    url: ({
      outletSlug,
      membershipSubscriptionId
    }: OutletSlug & {
      membershipSubscriptionId: string
    }) =>
      legacyAppRoot +
      `outlets/${outletSlug}/memberships/subscriptions/${membershipSubscriptionId}`
  },
  PURCHASED_CLASS_PASS: {
    id: 'PURCHASED_CLASS_PASS',
    url: ({
      outletSlug,
      purchasedClassPassId
    }: OutletSlug & {
      purchasedClassPassId: string
    }) =>
      legacyAppRoot +
      `outlets/${outletSlug}/vouchers/purchased_class_passes/${purchasedClassPassId}`
  },
  DEEP_LINKS: {
    id: 'DEEP_LINKS',
    url: ({ outletSlug, widgetSlug }: OutletSlug & { widgetSlug: string }) =>
      legacyAppRoot + `outlets/${outletSlug}/widgets/${widgetSlug}/deep_links`
  },
  DISCOUNT_CODES: {
    id: 'DISCOUNT_CODE',
    url: ({
      outletSlug,
      discountCodeId
    }: OutletSlug & {
      discountCodeId: string
    }) =>
      legacyAppRoot +
      `outlets/${outletSlug}/vouchers/discount_codes/${discountCodeId}`
  }
}

/**
 * Index by nextjs pathname i.e.
 * const route = useRouter().pathname
 *
 * This is going to be added to for use with determining layouts and access rights (permissions), redirects
 * etc.
 */
const routes = {
  HOME: {
    // NOTE - this isn't really out long term 'home' but we need somewhere to direct our login to for when we test things
    id: 'HOME',
    pathname: '/outlets',
    url: () => `/outlets`,
    authRequired: true
  },
  NOTIFICATIONS: {
    id: 'NOTIFICATIONS',
    pathname: '/notifications',
    url: ({ outletSlug }: OutletSlug) =>
      outletSlug ? `/outlets/${outletSlug}/notifications` : '/notifications',
    authRequired: true
  },
  SIGN_IN: {
    id: 'SIGN_IN',
    pathname: '/sign-in',
    url: () => '/sign-in'
  },
  SIGN_UP_ABOUT_YOU: {
    id: 'SIGN_UP_ABOUT_YOU',
    pathname: '/sign_up/about_you',
    url: () => '/sign_up/about_you'
  },
  SIGN_UP_ACCOUNT_CREATION: {
    id: 'SIGN_UP_ACCOUNT_CREATION',
    pathname: '/sign_up/account_creation',
    url: ({ firstName, lastName }: { firstName: string; lastName: string }) =>
      `/sign_up/account_creation?${objectToQueryString({
        firstName,
        lastName
      })}`
  },
  SIGN_UP_BUSINESS_DETAILS: {
    id: ' SIGN_UP_BUSINESS_DETAILS',
    pathname: '/sign_up/business_details',
    url: () => '/sign_up/business_details'
  },
  SIGN_UP_BUSINESS_ADDRESS: {
    id: 'SIGN_UP_BUSINESS_ADDRESS',
    pathname: '/sign_up/business_address',
    url: ({
      country,
      businessName,
      linkToSite,
      phoneNumber
    }: {
      country: string
      businessName: string
      linkToSite: string
      phoneNumber: string
    }) =>
      `/sign_up/business_address?${objectToQueryString({
        country,
        businessName,
        linkToSite,
        phoneNumber
      })}`
  },
  SIGN_UP_CREATE_YOUR_EXPERIENCE: {
    id: 'SIGN_UP_CREATE_YOUR_EXPERIENCE',
    pathname: '/sign_up/create_your_experience',
    url: ({ outletSlug }: OutletSlug) =>
      `/sign_up/create_your_experience?${objectToQueryString({
        outletSlug
      })}`
  },
  STYLEGUIDE: {
    id: 'STYLEGUIDE',
    pathname: '/styleguide',
    url: () => '/styleguide'
  },
  SEARCH: {
    id: 'SEARCH',
    pathname: '/outlets/[outletSlug]/search',
    url: ({ outletSlug }: OutletSlug) => `/outlets/${outletSlug}/search`,
    authRequired: true
  },
  INSTRUCTORS: {
    id: 'INSTRUCTORS',
    pathname: '/outlets/[outletSlug]/instructors',
    url: ({ outletSlug }: OutletSlug) => `/outlets/${outletSlug}/instructors`
  },
  INSTRUCTOR: {
    id: 'INSTRUCTOR',
    pathname: '/outlets/[outletSlug]/instructors/[instructorId]/edit',
    url: ({
      outletSlug,
      instructorId
    }: {
      outletSlug: string
      instructorId: string
    }) => `/outlets/${outletSlug}/instructors/${instructorId}/edit`,
    authRequired: true
  },
  POS_PAYMENT: {
    id: 'POS_PAYMENT',
    pathname: '/outlets/[outletSlug]/pos-payment/[basketId]',
    url: ({ outletSlug, basketId }: { outletSlug: string; basketId: string }) =>
      `/outlets/${outletSlug}/pos-payment/${basketId}`,
    authRequired: true
  },
  WIDGETS: {
    id: 'WIDGETS',
    url: ({ outletSlug }: OutletSlug) => `/outlets/${outletSlug}/widgets`,
    pathname: '/outlets/[outletSlug]/widgets',
    authRequired: true
  },
  NEW_WIDGET: {
    id: 'WIDGETS',
    url: ({ outletSlug }: OutletSlug) => `/outlets/${outletSlug}/widgets/new`,
    pathname: '/outlets/[outletSlug]/widgets/new',
    authRequired: true
  },
  EDIT_WIDGET: {
    id: 'WIDGETS',
    url: ({ outletSlug, widgetSlug }: OutletSlug & { widgetSlug: string }) =>
      `/outlets/${outletSlug}/widgets/${widgetSlug}/edit`,
    pathname: '/outlets/[outletSlug]/widgets/[widgetSlug]/edit',
    authRequired: true
  },
  DEMO_WIDGET: {
    id: 'WIDGETS',
    url: ({ outletSlug, widgetSlug }: OutletSlug & { widgetSlug: string }) =>
      `/outlets/${outletSlug}/widgets/${widgetSlug}/demo`,
    pathname: '/outlets/[outletSlug]/widgets/[widgetSlug]/demo',
    authRequired: true
  },
  NEW_LINE_ITEM: {
    id: 'NEW_LINE_ITEM',
    url: ({ outletSlug, basketId }: OutletSlug & { basketId?: string }) =>
      `/outlets/${outletSlug}/basket/line_items/new?${objectToQueryString({
        basketId
      })}`,
    pathname: '/outlets/[outletSlug]/basket/line_items/new',
    authRequired: true
  },
  EDIT_RENTAL_LINE_ITEM: {
    id: 'NEW_RENTAL_LINE_ITEM',
    url: ({
      outletSlug,
      rentalSlug,
      date,
      adultsGoing,
      childrenGoing,
      lineItemId,
      basketId
    }: OutletSlug & {
      rentalSlug: string
      date: string
      adultsGoing: string
      childrenGoing: string
      lineItemId?: string
      basketId?: string
    }) =>
      `/outlets/${outletSlug}/basket/line_items/edit/rental/${rentalSlug}?${objectToQueryString(
        { date, adultsGoing, childrenGoing, lineItemId, basketId }
      )}`,
    pathname: '/outlets/[outletSlug]/basket/line_items/edit/rental',
    authRequired: true
  },
  EDIT_ACTIVITY_LINE_ITEM: {
    id: 'NEW_ACTIVITY_LINE_ITEM',
    url: ({
      outletSlug,
      activitySlug,
      date,
      adultsGoing,
      childrenGoing,
      lineItemId,
      basketId
    }: OutletSlug & {
      activitySlug: string
      date: string
      adultsGoing: string
      childrenGoing: string
      lineItemId?: string
      basketId?: string
    }) =>
      `/outlets/${outletSlug}/basket/line_items/edit/activity/${activitySlug}?${objectToQueryString(
        { date, adultsGoing, childrenGoing, lineItemId, basketId }
      )}`,
    pathname: '/outlets/[outletSlug]/basket/line_items/edit/activity',
    authRequired: true
  },
  EDIT_STORE_ITEM_LINE_ITEM: {
    id: 'NEW_STORE_ITEM_LINE_ITEM',
    url: ({
      outletSlug,
      basketId
    }: OutletSlug & {
      basketId?: string
    }) =>
      `/outlets/${outletSlug}/basket/line_items/edit/store_items/?${objectToQueryString(
        { basketId }
      )}`,
    pathname: '/outlets/[outletSlug]/basket/line_items/edit/store_items',
    authRequired: true
  },
  CHECKOUT: {
    id: 'CHECKOUT',
    url: ({ outletSlug, basketId }: OutletSlug & { basketId: string }) =>
      `/outlets/${outletSlug}/basket/${basketId}/checkout`,
    pathname: '/outlets/[outletSlug]/basket/checkout',
    authRequired: true
  },
  LANDING_PAGES: {
    id: 'LANDING_PAGES',
    url: ({ pageSlug }: { pageSlug: string }) => `/${pageSlug}`,
    pathname: '/[...page]',
    authRequired: false
  },
  EDIT_CREDIT_VOUCHER_LINE_ITEM: {
    id: 'NEW_CREDIT_VOUCHER_LINE_ITEM',
    url: ({
      outletSlug,
      basketId
    }: OutletSlug & {
      basketId?: string
    }) =>
      `/outlets/${outletSlug}/basket/line_items/edit/credit_voucher/?${objectToQueryString(
        { basketId }
      )}`,
    pathname: '/outlets/[outletSlug]/basket/line_items/edit/credit_voucher',
    authRequired: true
  },
  EDIT_CLASS_PASS_LINE_ITEM: {
    id: 'NEW_CLASS_PASS_LINE_ITEM',
    url: ({
      outletSlug,
      basketId
    }: OutletSlug & {
      basketId?: string
    }) =>
      `/outlets/${outletSlug}/basket/line_items/edit/class_pass/?${objectToQueryString(
        { basketId }
      )}`,
    pathname: '/outlets/[outletSlug]/basket/line_items/edit/class_pass',
    authRequired: true
  }
}

export default routes

/**
 * This is a mirror of routes by is reassigned instead by pathname instead of our normal `id`.
 *
 * We could have indexed all of the routes by pathname originally but it would make for ugly code
 * i.e. router.push(routeUrls['/outlets/[outletSlug]/notifications'].url({outletSlug}))
 * vs router.push(routeUrls.NOTIFICATIONS.url({outletSlug})).
 *
 * With another proxy object we can leverage the same object values again but just index slightly
 * differently.
 */
export const routesByPathname: { [pathnameId: string]: Route } = Object.values(
  routes
).reduce((obj, route) => ({ ...obj, [route.pathname]: route }), {})
